import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../utils/supabaseClient";
import {
  getSupportMessages,
  sendSupportMessage,
  markSupportRead,
} from "../Services/supportService";

/**
 * Live support chat for a single conversation.
 * Loads history over REST, then subscribes to postgres_changes for new rows.
 *
 * @param {string|null} conversationId
 */
export const useSupportChat = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const seenIds = useRef(new Set());

  const upsertMessage = useCallback((msg) => {
    if (!msg || seenIds.current.has(msg.id)) return;
    seenIds.current.add(msg.id);
    setMessages((prev) =>
      [...prev, msg].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      )
    );
  }, []);

  // Load history whenever the conversation changes.
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      seenIds.current = new Set();
      return;
    }
    let cancelled = false;
    setLoading(true);
    seenIds.current = new Set();
    getSupportMessages(conversationId)
      .then((data) => {
        if (cancelled) return;
        const list = data || [];
        list.forEach((m) => seenIds.current.add(m.id));
        setMessages(list);
        markSupportRead(conversationId).catch(() => {});
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // Realtime subscription.
  useEffect(() => {
    if (!conversationId || !supabase) return;
    const channel = supabase
      .channel(`support:${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          upsertMessage(payload.new);
          markSupportRead(conversationId).catch(() => {});
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, upsertMessage]);

  const send = useCallback(
    async ({ body, file }) => {
      if (!conversationId) return;
      setSending(true);
      try {
        const msg = await sendSupportMessage({ conversationId, body, file });
        upsertMessage(msg); // instant echo; realtime dedupes by id
        return msg;
      } finally {
        setSending(false);
      }
    },
    [conversationId, upsertMessage]
  );

  return { messages, loading, sending, send };
};
